import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Info,
  ArrowRight,
  Users,
  ChevronLeft,
  Save,
  Trash2,
  Plus,
  Ban,
} from "lucide-react";
import { saveProfessionalTaxConfig } from "@/services/adminService";
import { ensureSlabIds } from "./TaxHelpers";

/* ---------------- Component ---------------- */

/**
 * Renders the Professional Tax configuration list and editor views.
 * @param {object} props
 * @param {array} props.states - List of PT configurations from the backend.
 * @param {boolean} props.isViewMode - Whether to show the list/editor screen (true) or the disabled prompt (false).
 * @param {function} props.setViewMode - Function to toggle view/edit mode in the main component.
 * @param {function} props.loadConfig - Function to reload the PT config from the backend.
 * @returns {JSX.Element}
 */
const ProfessionalTaxConfiguration = ({
  states,
  isViewMode,
  setViewMode,
  loadConfig,
  canEditTaxConfig,
}) => {
  const STATES = states || [];

  const [activeView, setActiveView] = useState("list"); // list | editor
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedState, setSelectedState] = useState(null);

  // Sync back to list view when isViewMode changes (e.g., config is disabled or loaded)
  useEffect(() => {
    if (isViewMode) {
      setActiveView("list");
      setSelectedState(null);
    }
  }, [isViewMode]);

  const handleEdit = useCallback((stateObj) => {
    setSelectedState(stateObj);
    setActiveView("editor");
  }, []);

  /* -----------------------------
   * UI: LIST VIEW
   * ----------------------------- */
  const ListView = () => {
    // Memoize filtered states to prevent recalculation
    const filteredStates = useMemo(() => {
      return STATES.filter((s) =>
        s.state.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }, [STATES, searchQuery]);

    return (
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex justify-between items-end flex-col sm:flex-row gap-4">
          <div>
            <h1 className="text-2xl font-bold text-black dark:text-white">
              Professional Tax
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              This tax is levied on an employee's income by the State
              Government. Tax slabs differ in each state.
            </p>
          </div>
          {/* Add search and filters here if needed */}
        </div>

        {/* STATES GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {STATES.length === 0 && (
            <div className="text-slate-500 text-sm col-span-full">
              No PT configuration found.
            </div>
          )}

          {filteredStates.map((state) => (
            <div key={state.state}>
              {/* If PT is NOT applicable */}
              {!state.is_pt_applicable ? (
                <Card className="border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col justify-center h-full">
                  <CardContent className="p-0 flex flex-col items-center gap-2 justify-center text-center">
                    <Ban className=" text-red-500" />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Professional Tax is not applicable for {state.state}.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card onClick={() => {
                    if (!canEditTaxConfig) return;
                    handleEdit(state);
                  }}>
                  <CardContent className="p-0 group cursor-pointer hover:shadow-lg transition-shadow">
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-4">
                        <div className="w-10 h-10 rounded-lg bg-[#3194A0] text-white flex items-center justify-center font-bold">
                          {state.state_code ||
                            state.state.slice(0, 2).toUpperCase()}
                        </div>

                        <span className="text-xs bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full text-emerald-700">
                          Configured
                        </span>
                      </div>

                      <h3 className="font-semibold text-black dark:text-white">
                        {state.state}
                      </h3>

                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Users size={14} /> {state.staffCount || 0} Employees
                        </span>

                        <ArrowRight
                          size={16}
                          className="text-slate-300 group-hover:text-[#3194A0] transition-all opacity-0 group-hover:opacity-100 group-hover:translate-x-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ---------------------------------------
  PROFESSIONAL TAX - EDITOR VIEW
  ---------------------------------------- */
  const EditorView = () => {
    if (!selectedState) return null;

    // Use local state to prevent parent re-renders and manage unsaved changes
    const [localState, setLocalState] = useState(() => ({
      ...selectedState,
      // APPLY THE HELPER FUNCTION HERE to ensure slabs have IDs
      slabs: ensureSlabIds(selectedState.slabs),
    }));

    // Sync when selectedState changes (when opening editor)
    useEffect(() => {
      setLocalState({
        ...selectedState,
        slabs: ensureSlabIds(selectedState.slabs),
      });
    }, [selectedState.state]);

    const updateState = useCallback((field, value) => {
      setLocalState((prev) => ({ ...prev, [field]: value }));
    }, []);

    const updateSlab = useCallback((id, field, value) => {
      setLocalState((prev) => ({
        ...prev,
        slabs: prev.slabs.map((s) =>
          s.id === id ? { ...s, [field]: value } : s,
        ),
      }));
    }, []);

    const addSlab = useCallback(() => {
      setLocalState((prev) => ({
        ...prev,
        slabs: [
          ...prev.slabs,
          {
            id: Date.now().toString(),
            is_female_exempted: false,
            start_amount: 0,
            end_amount: null,
            tax: 0,
            deduction_months: [],
          },
        ],
      }));
    }, []);

    const removeSlab = useCallback((id) => {
      setLocalState((prev) => ({
        ...prev,
        slabs: prev.slabs.filter((x) => x.id !== id),
      }));
    }, []);

    const save = async () => {
      try {
        // Assume saveProfessionalTaxConfig is implemented in adminService
        await saveProfessionalTaxConfig(localState);

        // Reload data from backend to refresh the list/view state
        await loadConfig("professional_tax");
        setSelectedState(localState);
        setActiveView("list");
      } catch (err) {
        console.error("Save error:", err);
      }
    };

    const cancel = () => {
      setActiveView("list");
      setSelectedState(null);
    };

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return (
      <div className="space-y-6 animate-in fade-in">
        {/* HEADER */}
        <div className="flex justify-between sticky top-[64px] bg-white dark:bg-card py-4 border-b z-30 px-2 -mx-2">
          <div className="flex gap-4 items-center">
            <Button
              variant="outline"
              size="icon"
              onClick={cancel}
              className="rounded-full bg-white dark:bg-secondary"
            >
              <ChevronLeft size={18} />
            </Button>

            <div className="flex flex-col">
              <h2 className="text-xl font-bold text-slate-900 dark:text-foreground flex items-center gap-2">
                {localState.state}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!canEditTaxConfig) return;
                save();
              }}
              disabled={!canEditTaxConfig}
              className="gap-2"
            >
              <Save size={16} /> Save Changes
            </Button>
          </div>
        </div>

        {/* BODY */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT COLUMN – GENERAL SETTINGS */}
          <div className="space-y-6">
            <Card className="p-6 border-l-4 border-l-[#3194A0]">
              <h3 className="font-semibold mb-1">General Settings</h3>
              <p className="text-sm text-slate-500 mb-6">
                Basic rules for tax calculation.
              </p>

              <div className="space-y-5">
                {/* FREQUENCY */}
                <div className="space-y-2">
                  <Label>Deduction Frequency</Label>
                  <Select
                    value={localState.deduction_frequency || "Monthly"}
                    onValueChange={(value) =>
                      updateState("deduction_frequency", value)
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="half-yearly">Half-Yearly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Add other general settings here */}
              </div>
            </Card>

            {/* LEGAL INFO BOX */}
            <div className="bg-blue-50 dark:bg-blue-500/40 border border-blue-100 rounded-lg p-4 flex gap-3">
              <Info className="text-blue-600 dark:text-blue-100" size={18} />
              <div className="text-sm text-blue-900 dark:text-blue-200">
                <p className="font-semibold mb-1">Legal Limit Note</p>
                <p className="text-xs text-blue-700 dark:text-blue-100 leading-relaxed">
                  As per Article 276, PT cannot exceed{" "}
                  <strong>₹2,500 per annum</strong> per individual.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — SLABS TABLE */}
          <div className="lg:col-span-2 h-full">
            <Card className="flex flex-col h-full">
              {/* HEADER */}
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Tax Slabs</h3>
                  <p className="text-sm text-slate-500">
                    Define salary ranges and tax amounts.
                  </p>
                </div>

                <Button
                  size="sm"
                  onClick={() => {
                    if (!canEditTaxConfig) return;
                    addSlab();
                  }}
                  disabled={!canEditTaxConfig}
                  className="gap-2"
                >
                  <Plus size={14} /> Add Slab
                </Button>
              </div>

              {/* TABLE */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-card border-b text-xs uppercase">
                    <tr>
                      <th className="px-6 py-3 text-left">Gender</th>
                      <th className="px-6 py-3 text-left">Min Salary</th>
                      <th className="px-6 py-3 text-left">Max Salary</th>
                      <th className="px-6 py-3 text-left">Tax Amount</th>
                      <th className="px-6 py-3 text-left">Frequency</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {localState.slabs
                      .slice()
                      .sort(
                        (a, b) => (a.start_amount || 0) - (b.start_amount || 0),
                      )
                      .map((slab) => {
                        const months = slab.deduction_months || [];
                        const frequencyLabel = (() => {
                          if (months.length === 0) return "—";
                          if (months.length === 12) return "All Months (12)";
                          if (months.length === 1) return `${months[0]}`;
                          if (months.length === 2)
                            return `${months[0]} & ${months[1]}`;
                          return `${months[0]} + ${months.length - 1} more`;
                        })();

                        const frequencyClass = (() => {
                          if (months.length === 12)
                            return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
                          if (months.length === 2)
                            return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
                          if (months.length === 1)
                            return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300";
                          return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300";
                        })();

                        return (
                          <tr
                            key={slab.id}
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          >
                            {/* GENDER */}
                            <td className="px-6 py-2">
                              <Select
                                disabled={!canEditTaxConfig}
                                value={slab.is_female_exempted ? "Male" : "All"}
                                onValueChange={(value) =>
                                  updateSlab(
                                    slab.id,
                                    "is_female_exempted",
                                    value === "Male",
                                  )
                                }
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Gender" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="All">All</SelectItem>
                                  <SelectItem value="Male">Male</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>

                            {/* MIN SALARY */}
                            <td className="px-6 py-2">
                              <Input
                                disabled={!canEditTaxConfig}
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.start_amount || 0}
                                onChange={(e) =>
                                  updateSlab(
                                    slab.id,
                                    "start_amount",
                                    Number(e.target.value),
                                  )
                                }
                                className="w-32"
                              />
                            </td>

                            {/* MAX SALARY */}
                            <td className="px-6 py-2">
                              <Input
                                disabled={!canEditTaxConfig}
                                type="text"
                                placeholder="And above"
                                value={slab.end_amount ?? ""}
                                onChange={(e) =>
                                  updateSlab(
                                    slab.id,
                                    "end_amount",
                                    e.target.value === ""
                                      ? null
                                      : Number(e.target.value),
                                  )
                                }
                                className="w-32"
                              />
                            </td>

                            {/* TAX AMOUNT */}
                            <td className="px-6 py-2">
                              <Input
                                disabled={!canEditTaxConfig}
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.tax || 0}
                                onChange={(e) =>
                                  updateSlab(
                                    slab.id,
                                    "tax",
                                    Number(e.target.value),
                                  )
                                }
                                className="w-32"
                              />
                            </td>

                            {/* FREQUENCY INDICATOR (Should be changed to a Month picker/Multi-select in a real app) */}
                            <td className="px-6 py-2">
                              <span
                                className={`text-xs font-medium px-2 py-1 rounded-md ${frequencyClass}`}
                              >
                                {frequencyLabel}
                              </span>
                            </td>

                            {/* DELETE BUTTON */}
                            <td className="px-6 py-2 text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={!canEditTaxConfig}
                                onClick={() => {
                                  if (!canEditTaxConfig) return;
                                  removeSlab(slab.id);
                                }}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>

                {localState.slabs.length === 0 && (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No tax slabs configured. Click "Add Slab" to create one.
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  /* -----------------------------
   * FINAL RENDER
   * ----------------------------- */
  return activeView === "list" ? <ListView /> : <EditorView />;
};

export default ProfessionalTaxConfiguration;
